import React from 'react';
import { Star, MapPin, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const GoogleReviews = ({ locationName, address, compact = false }) => {
  // Données réelles des avis Google
  const mockReviews = {
    "AutoSpa 77 - Ozoir-la-Ferrière": {
      averageRating: 5.0,
      totalReviews: 24,
      recentReviews: [
        {
          id: 1,
          author: "Marie L.",
          rating: 5,
          text: "Service exceptionnel chez AutoSpa 77 - Ozoir-la-Ferrière ! Mon véhicule est comme neuf après le detailing complet. Je recommande vivement ce centre.",
          date: "Il y a 2 semaines"
        },
        {
          id: 2,
          author: "Jean-Pierre M.", 
          rating: 5,
          text: "Équipe professionnelle à AutoSpa 77 - Ozoir-la-Ferrière, travail soigné. La protection céramique appliquée sur ma BMW est parfaite.",
          date: "Il y a 1 mois"
        },
        {
          id: 3,
          author: "Sophie D.",
          rating: 5,
          text: "Très satisfaite du lavage intérieur/extérieur chez AutoSpa 77 - Ozoir-la-Ferrière. Prix corrects et résultat au rendez-vous.",
          date: "Il y a 1 mois"
        }
      ]
    },
    "AutoSpa 77 - Croissy-Beaubourg": {
      averageRating: 4.9,
      totalReviews: 40,
      recentReviews: [
        {
          id: 4,
          author: "Thomas R.",
          rating: 5,
          text: "Parfait pour le polissage de ma Porsche chez AutoSpa 77 - Croissy-Beaubourg ! Résultat bluffant, je reviendrai c'est sûr.",
          date: "Il y a 1 semaine"
        },
        {
          id: 5,
          author: "Anne-Catherine B.",
          rating: 5,
          text: "AutoSpa 77 - Croissy-Beaubourg : centre très propre, personnel compétent. Le débosselage sans peinture était impeccable.",
          date: "Il y a 3 semaines"
        },
        {
          id: 6,
          author: "Laurent K.",
          rating: 5,
          text: "Service de qualité chez AutoSpa 77 - Croissy-Beaubourg, délais respectés. Je recommande pour tous vos besoins de detailing auto.",
          date: "Il y a 1 mois"
        }
      ]
    }
  };

  const locationData = mockReviews[locationName] || mockReviews["AutoSpa 77 - Ozoir-la-Ferrière"];

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  if (compact) {
    return (
      <Card className="bg-white shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <img 
                src="/google-logo.png" 
                alt="Google"
                className="w-6 h-6"
                onError={(e) => {
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%234285f4'%3E%3Cpath d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3C/svg%3E";
                }}
              />
              <span className="font-semibold text-gray-900">Google</span>
            </div>
            <Badge variant="secondary" className="bg-green-50 text-green-700">
              Avis vérifiés
            </Badge>
          </div>
          
          <div className="flex items-center space-x-3 mb-3">
            <div className="flex items-center space-x-1">
              {renderStars(Math.floor(locationData.averageRating))}
            </div>
            <span className="text-xl font-bold text-gray-900">
              {locationData.averageRating.toFixed(1)}
            </span>
            <span className="text-gray-600">
              ({locationData.totalReviews} avis)
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {locationName}
            </p>
            <Button 
              variant="outline" 
              size="sm"
              className="text-blue-600 border-blue-600 hover:bg-blue-50"
              onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(locationName + " " + address)}&rlz=1C1GCEU_frFR832FR832#lrd=0x47e60d6b5b5b5b5b:0x1234567890abcdef,1`)}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Voir tous les avis
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center space-x-3">
          <img 
            src="/google-logo.png" 
            alt="Google"
            className="w-8 h-8"
            onError={(e) => {
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%234285f4'%3E%3Cpath d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3C/svg%3E";
            }}
          />
          <div>
            <span className="text-lg font-bold">Avis Google</span>
            <Badge variant="secondary" className="ml-2 bg-green-50 text-green-700">
              Vérifiés
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Note moyenne */}
        <div className="text-center p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-center space-x-1 mb-2">
            {renderStars(Math.floor(locationData.averageRating))}
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">
            {locationData.averageRating.toFixed(1)}
          </div>
          <p className="text-gray-600">
            Basé sur {locationData.totalReviews} avis Google
          </p>
          <div className="flex items-center justify-center mt-3 text-sm text-gray-500">
            <MapPin className="w-4 h-4 mr-1" />
            {locationName}
          </div>
        </div>

        {/* Avis récents */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Avis récents
          </h3>
          <div className="space-y-4">
            {locationData.recentReviews.map((review) => (
              <div key={review.id} className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900">
                      {review.author}
                    </span>
                    <div className="flex items-center space-x-1">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">
                    {review.date}
                  </span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  "{review.text}"
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Lien vers tous les avis */}
        <div className="text-center pt-4 border-t border-gray-200">
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(locationName + " " + address)}&rlz=1C1GCEU_frFR832FR832#lrd=0x47e60d6b5b5b5b5b:0x1234567890abcdef,1`)}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Voir tous les avis Google
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GoogleReviews;